import { dealerTimeTooltipText, isValidTimeZone } from '@/utils'

const MIN_PLAUSIBLE_EPOCH_SECONDS = Date.UTC(1990, 0, 1) / 1000

function isPlausibleEpochSeconds(seconds: number): boolean {
  if (!Number.isFinite(seconds)) return false
  if (seconds < MIN_PLAUSIBLE_EPOCH_SECONDS) return false

  // Accept existing feed data slightly beyond today, but reject accidental far-future values
  // such as small counters being interpreted as years.
  const tenYearsFromNowSeconds = Math.floor(Date.now() / 1000) + 10 * 366 * 24 * 60 * 60
  return seconds <= tenYearsFromNowSeconds
}

/** Normalize SRP/feed date_in_stock_ts values to epoch seconds. */
export function normalizeEpochSeconds(value: unknown): number | undefined {
  if (value == null) return undefined

  if (typeof value === 'string' && value.trim() === '') return undefined

  if (typeof value === 'number' && Number.isFinite(value)) {
    const n = value
    if (n <= 0) return undefined
    const seconds = n >= 1_000_000_000_000 ? Math.floor(n / 1000) : Math.floor(n)
    return isPlausibleEpochSeconds(seconds) ? seconds : undefined
  }

  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (!trimmed) return undefined

    if (/^\d+$/.test(trimmed)) {
      const n = parseInt(trimmed, 10)
      if (!Number.isFinite(n) || n <= 0) return undefined
      const seconds = n >= 1_000_000_000_000 ? Math.floor(n / 1000) : n
      return isPlausibleEpochSeconds(seconds) ? seconds : undefined
    }

    const parsedMs = Date.parse(trimmed)
    if (Number.isNaN(parsedMs)) return undefined
    const seconds = Math.floor(parsedMs / 1000)
    return isPlausibleEpochSeconds(seconds) ? seconds : undefined
  }

  return undefined
}

export function dateInStockToInstantMs(value: unknown): number | null {
  const seconds = normalizeEpochSeconds(value)
  return seconds === undefined ? null : seconds * 1000
}

type TimestampShape = 'seconds' | 'milliseconds' | 'seconds_string' | 'milliseconds_string' | 'iso_or_other'

function resolveTimestampShape(originalValue: unknown): TimestampShape {
  if (typeof originalValue === 'number' && Number.isFinite(originalValue)) {
    const seconds = normalizeEpochSeconds(originalValue)
    if (seconds === undefined) return 'seconds'
    return originalValue >= 1_000_000_000_000 ? 'milliseconds' : 'seconds'
  }

  if (typeof originalValue === 'string') {
    const trimmed = originalValue.trim()
    if (/^\d+$/.test(trimmed)) {
      const n = parseInt(trimmed, 10)
      const seconds = normalizeEpochSeconds(trimmed)
      if (seconds === undefined) return 'seconds'
      return n >= 1_000_000_000_000 ? 'milliseconds_string' : 'seconds_string'
    }
    return 'iso_or_other'
  }

  return 'seconds'
}

function packTimestamp(seconds: number, shape: TimestampShape): unknown {
  switch (shape) {
    case 'milliseconds':
      return seconds * 1000
    case 'milliseconds_string':
      return String(seconds * 1000)
    case 'seconds_string':
      return String(seconds)
    case 'iso_or_other':
    case 'seconds':
    default:
      return seconds
  }
}

function getZonedDateTimeParts(instantMs: number, timeZone: string) {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })

  const parts = Object.fromEntries(
    formatter
      .formatToParts(new Date(instantMs))
      .filter((part) => part.type !== 'literal')
      .map((part) => [part.type, Number(part.value)]),
  )

  return {
    year: parts.year,
    month: parts.month,
    day: parts.day,
    hour: parts.hour,
    minute: parts.minute,
    second: parts.second,
  }
}

function formatDatePartsAsDateInput(parts: { year: number; month: number; day: number }): string {
  if (!Number.isFinite(parts.year) || !Number.isFinite(parts.month) || !Number.isFinite(parts.day)) {
    return ''
  }
  const pad = (part: number) => String(part).padStart(2, '0')
  return `${String(parts.year).padStart(4, '0')}-${pad(parts.month)}-${pad(parts.day)}`
}

/** UTC midnight for a YYYY-MM-DD string. Used as UTC fallback. */
export function zonedLocalDateMidnightToUtcSeconds(
  dateString: string,
  timeZone: string,
): number | undefined {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(dateString).trim())
  if (!match || !isValidTimeZone(timeZone)) return undefined

  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])

  let utcMs = Date.UTC(year, month - 1, day, 12, 0, 0)

  for (let attempt = 0; attempt < 48; attempt++) {
    const local = getZonedDateTimeParts(utcMs, timeZone)
    if (
      local.year === year &&
      local.month === month &&
      local.day === day &&
      local.hour === 0 &&
      local.minute === 0 &&
      local.second === 0
    ) {
      return Math.floor(utcMs / 1000)
    }

    const localDayIndex = Date.UTC(local.year, local.month - 1, local.day) / 86400000
    const targetDayIndex = Date.UTC(year, month - 1, day) / 86400000

    if (localDayIndex > targetDayIndex || (localDayIndex === targetDayIndex && local.hour > 0)) {
      utcMs -= 3600000
    } else {
      utcMs += 3600000
    }
  }

  return undefined
}

/** Format a timestamp-like value for HTML date inputs (YYYY-MM-DD, UTC). */
export function toDateInputValue(value: unknown): string {
  const seconds = normalizeEpochSeconds(value)
  if (seconds === undefined) return ''

  const date = new Date(seconds * 1000)
  if (Number.isNaN(date.getTime())) return ''

  const pad = (part: number) => String(part).padStart(2, '0')
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`
}

/** Dealer-local YYYY-MM-DD for date-only display and inputs. */
export function formatDealerDateOnly(value: unknown, dealerTimeZone?: string): string {
  const seconds = normalizeEpochSeconds(value)
  if (seconds === undefined) return ''

  const timeZone = String(dealerTimeZone || '').trim()
  if (!timeZone || !isValidTimeZone(timeZone)) {
    return toDateInputValue(value)
  }

  const parts = getZonedDateTimeParts(seconds * 1000, timeZone)
  return formatDatePartsAsDateInput(parts)
}

export function toDealerDateInputValue(value: unknown, dealerTimeZone?: string): string {
  return formatDealerDateOnly(value, dealerTimeZone)
}

export function dealerDateTooltipText(value: unknown, dealerTimeZone?: string): string {
  const instantMs = dateInStockToInstantMs(value)
  if (instantMs == null) return ''

  return dealerTimeTooltipText({
    value: instantMs,
    dealerTimeZone,
    kind: 'dateTime',
    label: 'Dealer time',
  })
}

export function isValidDateInputValue(value: unknown): boolean {
  if (value == null) return true
  const raw = String(value).trim()
  if (!raw) return true
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return false
  return !Number.isNaN(Date.parse(`${raw}T00:00:00.000Z`))
}

/** Convert a date input value back to the canonical SRP field shape (UTC midnight). */
export function fromDateInputValue(dateString: unknown, originalValue: unknown): unknown {
  const raw = String(dateString ?? '').trim()
  if (!raw) return null

  if (!isValidDateInputValue(raw)) {
    return originalValue ?? null
  }

  const seconds = Math.floor(Date.parse(`${raw}T00:00:00.000Z`) / 1000)
  if (!Number.isFinite(seconds) || seconds <= 0) {
    return originalValue ?? null
  }

  return packTimestamp(seconds, resolveTimestampShape(originalValue))
}

/** Convert a dealer-local date input value back to the canonical SRP field shape. */
export function fromDealerDateInputValue(
  dateString: unknown,
  originalValue: unknown,
  dealerTimeZone?: string,
): unknown {
  const raw = String(dateString ?? '').trim()
  if (!raw) return null

  if (!isValidDateInputValue(raw)) {
    return originalValue ?? null
  }

  const timeZone = String(dealerTimeZone || '').trim()
  const seconds =
    timeZone && isValidTimeZone(timeZone)
      ? zonedLocalDateMidnightToUtcSeconds(raw, timeZone)
      : undefined

  const resolvedSeconds =
    seconds ??
    (() => {
      const fallback = fromDateInputValue(raw, originalValue)
      return normalizeEpochSeconds(fallback)
    })()

  if (resolvedSeconds === undefined || !Number.isFinite(resolvedSeconds) || resolvedSeconds <= 0) {
    return originalValue ?? null
  }

  return packTimestamp(resolvedSeconds, resolveTimestampShape(originalValue))
}

export function dealerTodayDateInputValue(dealerTimeZone?: string): string {
  const timeZone = String(dealerTimeZone || '').trim()
  if (timeZone && isValidTimeZone(timeZone)) {
    return formatDealerDateOnly(Date.now(), timeZone)
  }
  return toDateInputValue(Date.now())
}

export function isFutureDealerDateInputValue(dateString: unknown, dealerTimeZone?: string): boolean {
  const raw = String(dateString ?? '').trim()
  if (!raw || !isValidDateInputValue(raw)) return false
  return raw > dealerTodayDateInputValue(dealerTimeZone)
}

/** Normalize optional positive integer fields such as passenger_capacity. */
export function normalizeIntegerInput(value: unknown): number | null {
  if (value == null) return null
  if (typeof value === 'string' && value.trim() === '') return null

  const parsed =
    typeof value === 'number' && Number.isFinite(value)
      ? value
      : parseInt(String(value).trim(), 10)

  if (!Number.isFinite(parsed)) return null

  const rounded = Math.trunc(parsed)
  return rounded > 0 ? rounded : null
}

export function dateInStockHelperText(dealerTimeZone?: string): string {
  const timeZone = String(dealerTimeZone || '').trim()
  if (timeZone && isValidTimeZone(timeZone)) {
    return `Uses dealer timezone: ${timeZone}`
  }
  return 'Dealer timezone not configured.'
}
